import { useEffect, useRef, useState } from 'react';
import { shallow } from 'zustand/shallow';
import { ConfirmationDialog } from '@components/dialog/ConfirmationDialog';
import { CancelationModal, DeclinationModal, IneligibilitiesModal, ChangeLogModal } from '@components/modals';

import { Action } from '@pages/QuoteDetails/constants/QuoteDetails.constants';
import {
  RoleManagementDialog,
  AcceptQuoteDialog,
  ResetQuoteDefaultsDialog,
  GenerateDocumentDialog,
} from '@pages/QuoteDetails/dialogs';
import { CopyQuoteDialog } from '@pages/QuoteDetails/dialogs/CopyQuoteDialog';
import { SwitchOfferingDialog } from '@pages/QuoteDetails/dialogs/SwitchOfferingDialog';
import { ReassignQuoteDialog } from '@pages/QuoteDetails/dialogs/ReassignQuoteDialog';
import { ConvertRenewalToNewBusinessDialog } from '@pages/QuoteDetails/dialogs/ConvertRenewalToNewBusinessDialog';
import { ReleaseQuoteDialog } from '@pages/QuoteDetails/dialogs/ReleaseQuoteDialog';
import { useStore } from '@store/GlobalStore';
import { getFormattedPolicyNumber } from '@utils/policy/getFormattedPolicyNumber';
import { QuoteStatus, QuoteType, TQuoteApiResponse } from '@models/quotes';
import { ToastType } from '@components/toast';
import { useUpdateQuote } from '@services/quotes';
import { getApiErrorMessage } from '@utils/getApiErrorMessage';
import { enqueueSnackbar } from 'notistack';
import { IDialogState } from '@pages/QuoteDetails/types';
import {
  useDeletePolicyTransaction,
  useDisassociateConditionalRenewalQuoteId,
  useGetPolicy,
  useSearchPolicyTransactions,
} from '@services/policy';
import { useSearchParams } from 'react-router-dom';
import { IneligibleExposureItemListDialog } from '@pages/QuoteDetails/dialogs/isIneligibleExposureItemListDialog';
import { GrantCustomerAccessDialog } from '@pages/QuoteDetails/dialogs/GrantCustomerAccessDialog';
import { ReactivateQuoteDialogWrapper } from '@components/modals/ReactivateQuoteModals/ReactivateQuoteDialogWrapper';
import { RateChangeAnalysisModal } from '@components/modals/RateChangeAnalysisModal/RateChangeAnalysisModal';
import { ExternalSentEmailsModal } from '@components/modals/ExternalSentEmailsModal/ExternalSentEmailsModal';
import { QuoteDeclarationsModal } from '@components/modals/QuoteDeclarationsModal/QuoteDeclarationsModal';
import { useSearchPolicyTerms } from '@services/policyTerm/useSearchPolicyTerms';
import { CreatePolicyTransactionDialog } from '@pages/PolicyDetails/dialogs';
import { PolicyTransactionStatus, PolicyTransactionTypes, RenewDialogType } from '@models/policyTransaction';
import { UpdateNamedInsuredModal } from '@pages/CustomerDetails/dialogs/UpdateNamedInsuredModal';
import { useGetCustomer } from '@services/customer';

export type QDDialogsProps = {
  quote: TQuoteApiResponse;
  quoteId: string;
  dialogState: IDialogState;
  onSetDialogState: React.Dispatch<React.SetStateAction<IDialogState>>;
  onConfirmExit: () => void;
  onStatusRead: () => void;
};

export const QDDialogs = ({
  quote,
  quoteId,
  dialogState,
  onSetDialogState,
  onConfirmExit,
  onStatusRead,
}: QDDialogsProps) => {
  const [userAuth] = useStore((state) => [state.userAuth], shallow);
  const [searchParams, setSearchParams] = useSearchParams();
  const { isInternalUser } = userAuth;

  const shouldOpenNameChangeModal = searchParams.get('requestNameChange') === 'true';
  const [isNameChangeModalOpen, setIsNameChangeModalOpen] = useState(false);
  const { customer } = useGetCustomer(quote?.customer?.id ?? '');

  useEffect(() => {
    if (!shouldOpenNameChangeModal) {
      return;
    }

    setIsNameChangeModalOpen(true);
    setSearchParams(
      (currentParams) => {
        const next = new URLSearchParams(currentParams);
        next.delete('requestNameChange');
        return next;
      },
      { replace: true },
    );
  }, [shouldOpenNameChangeModal, setSearchParams]);

  const handleCloseNameChangeModal = () => {
    setIsNameChangeModalOpen(false);
    setSearchParams(
      (currentParams) => {
        const next = new URLSearchParams(currentParams);
        next.delete('requestNameChange');
        return next;
      },
      { replace: true },
    );
  };

  const { updateQuote, isPending: isUpdateQuoteLoading } = useUpdateQuote();
  const { deletePolicyTransaction, isPending: isDeletePolicyTransactionPending } = useDeletePolicyTransaction();
  const { disassociateConditionalRenewalQuoteId, isPending: isDisassociatePending } =
    useDisassociateConditionalRenewalQuoteId();
  const {
    confirmDialog,
    actionDialog,
    cancelQuoteDetails,
    declineQuoteDetails,
    declinedRejectedQuoteDetails,
    showChangelogDialog,
    isQuoteIneligibilitiesOpen,
    isRateChangeAnalysisOpen,
  } = dialogState;

  const { data: policy } = useGetPolicy({
    policyId: isInternalUser && quote ? quote.policyId : undefined,
  });
  const { policyTransactions } = useSearchPolicyTransactions({
    policyId: policy?.id ?? '',
    includeCanceled: false,
    pageLimit: 100,
  });
  const { policyTerms } = useSearchPolicyTerms({
    queryParams: { pageLimit: 100 },
    policyId: policy?.id ?? '',
  });

  const { policyTransactions: conditionalRenewalTransactions } = useSearchPolicyTransactions(
    {
      policyId: quote?.policyId ?? '',
      transactionType: PolicyTransactionTypes.ConditionalRenewal,
      status: [PolicyTransactionStatus.Processed],
      includeCanceled: false,
      pageLimit: 10,
    },
    quote?.quoteType === QuoteType.Renewal && !!quote?.policyId,
  );

  const processedConditionalRenewal = conditionalRenewalTransactions?.items?.find(
    (t) => t.policyConditionalRenewalTransaction?.renewalQuoteId === quote?.id,
  );

  // Keep a ref so that async callbacks always read the latest value.
  const processedConditionalRenewalRef = useRef(processedConditionalRenewal);
  processedConditionalRenewalRef.current = processedConditionalRenewal;

  // Tracks whether the user has already responded to the conditional renewal dialog
  // for the current cancel/decline action. Derived state drives the actual dialog visibility.
  const [conditionalRenewalDialogHandled, setConditionalRenewalDialogHandled] = useState(false);

  const isCancelOrDeclinePending = !!cancelQuoteDetails.cancelQuoteId || !!declineQuoteDetails.declineQuoteId;

  // Show the conditional renewal dialog first when cancel/decline is triggered and a processed
  // conditional renewal exists — derived in render so there's no flash of the wrong dialog.
  const showConditionalRenewalDialog =
    isCancelOrDeclinePending && !!processedConditionalRenewal && !conditionalRenewalDialogHandled;

  // Reset handled state when the cancel/decline dialogs are closed so repeat actions work correctly.
  useEffect(() => {
    if (!isCancelOrDeclinePending) {
      setConditionalRenewalDialogHandled(false);
    }
  }, [isCancelOrDeclinePending]);

  const isConditionalRenewalActionLoading = isDeletePolicyTransactionPending || isDisassociatePending;

  const policyNumber = quote?.policyNumber?.policyNumber;

  const hideActionDialog = () => {
    onSetDialogState((prev) => ({
      ...prev,
      actionDialog: {
        show: false,
        type: null,
      },
    }));
  };

  const onCancelExit = () => {
    onSetDialogState((prev) => ({ ...prev, confirmDialog: { ...prev.confirmDialog, show: false } }));
  };

  const handleCloseDeclineQuoteDialog = () => {
    onSetDialogState((prev) => ({
      ...prev,
      declineQuoteDetails: {
        declineQuotePolicyNumber: '',
        declineQuoteId: '',
        declineQuoteStatus: '',
        declineQuoteCustomerId: '',
        declineQuoteBrokerageId: '',
        hasInternalOnlyAccessRestriction: false,
      },
    }));
  };

  const handleCloseCancelQuoteDialog = () => {
    onSetDialogState((prev) => ({
      ...prev,
      cancelQuoteDetails: {
        cancelQuoteId: '',
        cancelQuotePolicyNumber: '',
      },
    }));
  };

  const handleShowUpdateQuoteSnackbar = ({ isUpdateQuoteError }: { isUpdateQuoteError: boolean }) => {
    const snackbarMessage = `Updating quote roles has ${isUpdateQuoteError ? 'failed' : 'succeeded'}`;
    const updateQuoteResult = isUpdateQuoteError ? 'Error' : 'Success';
    const snackbarVariant = ToastType[updateQuoteResult];
    enqueueSnackbar(snackbarMessage, {
      preventDuplicate: true,
      variant: snackbarVariant,
    });
  };

  return (
    <>
      <IneligibleExposureItemListDialog
        quote={quote}
        isOpen={searchParams.get('ineligibleExposures') === 'true'}
        onCloseModal={() => {
          searchParams.delete('ineligibleExposures');
          setSearchParams(searchParams);
        }}
      />
      <CancelationModal
        isOpen={!!cancelQuoteDetails.cancelQuoteId && !showConditionalRenewalDialog}
        quoteId={cancelQuoteDetails.cancelQuoteId}
        policyNumber={cancelQuoteDetails.cancelQuotePolicyNumber}
        onCloseModal={handleCloseCancelQuoteDialog}
      />
      <DeclinationModal
        isOpen={!!declineQuoteDetails.declineQuoteId && !showConditionalRenewalDialog}
        quoteId={declineQuoteDetails.declineQuoteId}
        policyNumber={declineQuoteDetails.declineQuotePolicyNumber}
        currentStatusId={declineQuoteDetails.declineQuoteStatus}
        customerId={declineQuoteDetails.declineQuoteCustomerId}
        brokerageId={declineQuoteDetails.declineQuoteBrokerageId}
        onCloseModal={handleCloseDeclineQuoteDialog}
        hasInternalOnlyAccessRestriction={declineQuoteDetails?.hasInternalOnlyAccessRestriction ?? false}
      />
      <IneligibilitiesModal
        isOpen={isQuoteIneligibilitiesOpen}
        quoteId={quoteId}
        policyNumber={policyNumber}
        onCloseModal={() =>
          onSetDialogState((prev) => ({
            ...prev,
            isQuoteIneligibilitiesOpen: false,
          }))
        }
      />
      <RateChangeAnalysisModal
        isOpen={isRateChangeAnalysisOpen}
        quoteId={quoteId}
        policyNumber={policyNumber}
        quote={quote}
        onClose={() =>
          onSetDialogState((prev) => ({
            ...prev,
            isRateChangeAnalysisOpen: false,
          }))
        }
      />
      {quote && actionDialog.show && actionDialog.type === Action.ViewExternalSentEmails ? (
        <ExternalSentEmailsModal
          open={actionDialog.show}
          onClose={() => hideActionDialog()}
          context='quote'
          quote={quote}
        />
      ) : null}
      {quote && actionDialog.show && actionDialog.type === Action.ViewQuoteDeclarations ? (
        <QuoteDeclarationsModal open={actionDialog.show} onClose={() => hideActionDialog()} quoteId={quote.id} />
      ) : null}
      {quote && actionDialog.show && actionDialog.type === Action.ManageRoles ? (
        <RoleManagementDialog
          showDialog={actionDialog.show}
          closeLinkDialog={() => hideActionDialog()}
          quote={quote}
          onUpdateQuote={updateQuote}
          isUpdateQuoteLoading={isUpdateQuoteLoading}
          onShowUpdateQuoteSnackbar={handleShowUpdateQuoteSnackbar}
        />
      ) : null}
      {quote && actionDialog.show && actionDialog.type === Action.GrantCustomerAccess ? (
        <GrantCustomerAccessDialog isOpen={actionDialog.show} quote={quote} onClose={() => hideActionDialog()} />
      ) : null}
      {quote && actionDialog.show && actionDialog.type === Action.ResetQuoteDefaults ? (
        <ResetQuoteDefaultsDialog open={actionDialog.show} quote={quote} handleCloseDialog={hideActionDialog} />
      ) : null}
      {quote &&
      showChangelogDialog &&
      (quote.quoteType === QuoteType.Endorsement || quote.quoteType === QuoteType.Renewal) ? (
        <ChangeLogModal
          quote={quote}
          disabled={quote.status.name === QuoteStatus.Bound.name}
          isOpen={showChangelogDialog}
          setIsOpen={(newValue) => {
            onSetDialogState((prev) => ({
              ...prev,
              showChangelogDialog: newValue,
            }));
          }}
          policyNumber={
            policy ? getFormattedPolicyNumber(policy.policyNumberPrefix, policy.policyNumber.policyNumber) : '...'
          }
          sourceQuoteId={quote.quoteType === QuoteType.Renewal ? policy?.boundQuote?.id : undefined}
          hideDescription={quote.quoteType === QuoteType.Renewal}
          showFormsSection={quote.quoteType === QuoteType.Renewal || quote.quoteType === QuoteType.Endorsement}
          isQuoteRated={
            quote.status.id !== QuoteStatus.Submission.id && quote.status.id !== QuoteStatus.ReadyForRating.id
          }
        />
      ) : null}
      {quote && actionDialog.show && actionDialog.type === Action.CopyQuote ? (
        <CopyQuoteDialog quoteId={quote.id} handleClose={() => hideActionDialog()} />
      ) : null}
      {quote && actionDialog.show && actionDialog.type === Action.SwitchOffering ? (
        <SwitchOfferingDialog quoteId={quote.id} handleClose={() => hideActionDialog()} />
      ) : null}
      {quote && actionDialog.show && actionDialog.type === Action.ReassignCustomer ? (
        <ReassignQuoteDialog quoteId={quote.id} handleClose={() => hideActionDialog()} />
      ) : null}
      {quote && actionDialog.show && actionDialog.type === Action.ConvertRenewalToNewBusiness ? (
        <ConvertRenewalToNewBusinessDialog quote={quote} handleClose={() => hideActionDialog()} />
      ) : null}

      {quote && (
        <ReactivateQuoteDialogWrapper
          showReactivateDialog={actionDialog.show && actionDialog.type === Action.ReactivateQuote}
          declinedRejectedQuoteDetails={declinedRejectedQuoteDetails}
          onStatusRead={onStatusRead}
          hideActionDialog={hideActionDialog}
          quote={quote}
          policyNumber={
            policy
              ? getFormattedPolicyNumber(policy?.policyNumberPrefix, policy?.policyNumber.policyNumber)
              : `#${quote.policyNumber?.policyNumber}`
          }
          handleCloseReactivateDeclinedRejectedQuoteDialog={() =>
            onSetDialogState((prev) => ({
              ...prev,
              declinedRejectedQuoteDetails: {
                declinedRejectedQuotePolicyNumber: '',
                declinedRejectedQuoteId: '',
                declinedRejectedQuoteStatus: '',
              },
            }))
          }
        />
      )}
      {quote && (
        <AcceptQuoteDialog
          isOpen={actionDialog.show && actionDialog.type === Action.AcceptQuote}
          closeDialog={() => hideActionDialog()}
          quote={quote}
        />
      )}
      {quote && (
        <ReleaseQuoteDialog
          isOpen={actionDialog.show && actionDialog.type === Action.ReleaseQuote}
          policyNumber={
            policy
              ? getFormattedPolicyNumber(policy?.policyNumberPrefix, policy?.policyNumber.policyNumber)
              : `#${quote.policyNumber?.policyNumber}`
          }
          onClose={() => hideActionDialog()}
          quote={quote}
        />
      )}
      {quote && (
        <GenerateDocumentDialog
          open={actionDialog.show && actionDialog.type === Action.GenerateDocument}
          quote={quote}
          handleCloseDialog={hideActionDialog}
        />
      )}
      {quote && policy && actionDialog.show && actionDialog.type === Action.GenerateConditionalRenewal ? (
        <CreatePolicyTransactionDialog
          isOpen
          onCancel={hideActionDialog}
          policy={policy}
          policyTransactions={policyTransactions?.items ?? []}
          policyTerms={policyTerms?.items ?? []}
          dialogType={RenewDialogType.ConditionalRenewal}
          other={{
            renewalQuoteId: quote.id,
          }}
        />
      ) : null}
      {quote && policy && actionDialog.show && actionDialog.type === Action.MarkForNonrenewal ? (
        <CreatePolicyTransactionDialog
          isOpen
          onCancel={hideActionDialog}
          policy={policy}
          policyTransactions={policyTransactions?.items ?? []}
          policyTerms={policyTerms?.items ?? []}
          dialogType={RenewDialogType.DoNotRenew}
          other={{
            conditionalRenewalTransactionId: processedConditionalRenewal?.id,
          }}
        />
      ) : null}
      <ConfirmationDialog
        isOpen={showConditionalRenewalDialog}
        onCancel={() => {
          // User aborted — also close the pending cancel/decline dialog
          handleCloseCancelQuoteDialog();
          handleCloseDeclineQuoteDialog();
        }}
        isLoading={isConditionalRenewalActionLoading}
        description='A Conditional Renewal exists that is tied to this renewal. Do you want to cancel that Conditional Renewal as well?'
        buttonConfig={{
          confirmText: 'Yes',
          secondaryText: 'No',
          onConfirm: async () => {
            if (!processedConditionalRenewalRef.current) {
              setConditionalRenewalDialogHandled(true);
              return;
            }
            await deletePolicyTransaction(processedConditionalRenewalRef.current.id).catch(async (e) => {
              const message = await getApiErrorMessage(e, 'Failed to delete Conditional Renewal');
              enqueueSnackbar(message, {
                variant: ToastType.Error,
                persist: true,
              });
            });
            setConditionalRenewalDialogHandled(true);
          },
          onSecondaryButtonClick: async () => {
            if (!processedConditionalRenewalRef.current) {
              setConditionalRenewalDialogHandled(true);
              return;
            }
            await disassociateConditionalRenewalQuoteId(processedConditionalRenewalRef.current.id).catch(async (e) => {
              const message = await getApiErrorMessage(e, 'Failed to disassociate Conditional Renewal');
              enqueueSnackbar(message, {
                variant: ToastType.Error,
                persist: true,
              });
            });
            setConditionalRenewalDialogHandled(true);
          },
        }}
      />
      {isNameChangeModalOpen && customer && (
        <UpdateNamedInsuredModal
          open={isNameChangeModalOpen}
          onClose={handleCloseNameChangeModal}
          customer={customer}
          isExternalUser={!isInternalUser}
          quoteId={quote.id}
        />
      )}
      {confirmDialog.checkboxText ? (
        <ConfirmationDialog
          isOpen={confirmDialog.show}
          onCancel={onCancelExit}
          description={confirmDialog.description}
          checkboxText={confirmDialog.checkboxText}
          buttonConfig={{
            onConfirm: onConfirmExit,
            confirmText: confirmDialog.confirmText,
            secondaryText: confirmDialog.cancelText,
          }}
        />
      ) : (
        <ConfirmationDialog
          isOpen={confirmDialog.show}
          onCancel={onCancelExit}
          description={confirmDialog.description}
          buttonConfig={{
            onConfirm: onConfirmExit,
            confirmText: confirmDialog.confirmText,
            secondaryText: confirmDialog.cancelText,
          }}
        />
      )}
    </>
  );
};
